import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Create as CreateIcon,
  Update as UpdateIcon,
  CheckCircle as CompletedIcon,
  Delete as DeleteIcon,
  Assignment as AssignedIcon,
  SwapHoriz as StatusChangeIcon,
  FilterList as FilterIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { TaskHistory, TaskHistoryFilters, todoService } from '../../services/api/todo.service';

interface TaskHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
}

const TaskHistoryDialog: React.FC<TaskHistoryDialogProps> = ({
  open,
  onClose,
  taskId,
  taskTitle
}) => {
  const [history, setHistory] = useState<TaskHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TaskHistoryFilters>({});

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await todoService.getTaskHistory(taskId, filters);
      setHistory(data);
    } catch (err) {
      console.error('Error fetching task history:', err);
      setError('Failed to load task history');
    } finally {
      setLoading(false);
    }
  }, [taskId, filters]);

  useEffect(() => {
    if (open && taskId) {
      fetchHistory();
    }
  }, [open, taskId, fetchHistory]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <CreateIcon fontSize="small" color="success" />;
      case 'updated':
        return <UpdateIcon fontSize="small" color="info" />;
      case 'completed':
        return <CompletedIcon fontSize="small" color="success" />;
      case 'deleted':
        return <DeleteIcon fontSize="small" color="error" />;
      case 'assigned':
        return <AssignedIcon fontSize="small" color="primary" />;
      case 'status_changed':
        return <StatusChangeIcon fontSize="small" color="warning" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'success';
      case 'updated':
        return 'info';
      case 'completed':
        return 'success';
      case 'deleted':
        return 'error';
      case 'assigned':
        return 'primary';
      case 'status_changed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatActionText = (action: string) => {
    switch (action) {
      case 'created':
        return 'Created';
      case 'updated':
        return 'Updated';
      case 'completed':
        return 'Completed';
      case 'deleted':
        return 'Deleted';
      case 'assigned':
        return 'Assigned';
      case 'status_changed':
        return 'Status Changed';
      default:
        return action.charAt(0).toUpperCase() + action.slice(1).replace('_', ' ');
    }
  };

  const formatChanges = (changes: Record<string, any>) => {
    const changeItems = Object.entries(changes).map(([key, value]) => {
      if (typeof value === 'object' && value.from !== undefined && value.to !== undefined) {
        return `${key}: ${value.from} → ${value.to}`;
      }
      return `${key}: ${value}`;
    });
    return changeItems.join(', ');
  };

  const handleFilterChange = (field: keyof TaskHistoryFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryIcon color="primary" />
          <Typography variant="h6">
            Task History
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={() => setShowFilters(!showFilters)}
            size="small"
            color={showFilters ? 'primary' : 'default'}
          >
            <FilterIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <div>
            <Typography variant="subtitle1" gutterBottom>
              {taskTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete history of changes and actions
            </Typography>
          </div>

          {/* Filters */}
          {showFilters && (
            <Paper style={{ padding: 16, backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" gutterBottom>
                Filters
              </Typography>
              <Stack direction="row" spacing={2} style={{ marginBottom: 16 }}>
                <FormControl size="small" style={{ minWidth: 150 }}>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action?.[0] || ''}
                    onChange={(e) => handleFilterChange('action', e.target.value ? [e.target.value] : undefined)}
                    label="Action"
                  >
                    <MenuItem value="">All Actions</MenuItem>
                    <MenuItem value="created">Created</MenuItem>
                    <MenuItem value="updated">Updated</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="assigned">Assigned</MenuItem>
                    <MenuItem value="status_changed">Status Changed</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="From Date"
                  type="date"
                  value={filters.from_date || ''}
                  onChange={(e) => handleFilterChange('from_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  size="small"
                  label="To Date"
                  type="date"
                  value={filters.to_date || ''}
                  onChange={(e) => handleFilterChange('to_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <Button size="small" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Paper>
          )}

          {/* History List */}
          {loading && (
            <div style={{ textAlign: 'center', paddingTop: 24, paddingBottom: 24 }}>
              <Typography>Loading history...</Typography>
            </div>
          )}

          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          {!loading && !error && history.length === 0 && (
            <Alert severity="info">
              No history entries found for this task.
            </Alert>
          )}

          {!loading && !error && history.length > 0 && (
            <List style={{ maxHeight: 400, overflow: 'auto' }}>
              {history.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  <ListItem style={{ alignItems: 'flex-start' }}>
                    <ListItemIcon style={{ marginTop: 8 }}>
                      {getActionIcon(entry.action)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Chip
                            label={formatActionText(entry.action)}
                            size="small"
                            color={getActionColor(entry.action) as any}
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Stack spacing={1} style={{ marginTop: 8 }}>
                          {entry.changes && Object.keys(entry.changes).length > 0 && (
                            <Typography variant="body2">
                              Changes: {formatChanges(entry.changes)}
                            </Typography>
                          )}

                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon fontSize="small" color="disabled" />
                            <Typography variant="caption" color="text.secondary">
                              {entry.changed_by_name || entry.changed_by}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              • {format(new Date(entry.timestamp), 'MMM dd, yyyy at HH:mm')}
                            </Typography>
                          </Stack>

                          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Metadata: {JSON.stringify(entry.metadata)}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>

                  {index < history.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskHistoryDialog;